/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NotificationStatus } from 'enums/notification-status';

/** Metadata for scheduled charge notifications (nested at payload.metadata.metadata from API) */
export interface ScheduledChargeNotificationMetadata {
  chargeName?: string;
  currentAmount?: string;
  previousAmount?: string;
  hasAmountChange?: boolean;
  amountDifference?: {
    isIncrease?: boolean;
    /** Same amounts as previousAmount/currentAmount; some translations use these names */
    oldValue?: string | number;
    newValue?: string | number;
  };
}

export interface NotificationPayload {
  id: string;
  title: string;
  message?: string;
  status: NotificationStatus;
  date?: string; // Store as ISO string instead of Date object
  duration?: number;
  persist?: boolean;
  read?: boolean;
  /** Notification type from API (e.g. ScheduledChargeChange) for translation lookup */
  type?: string;
  /** Metadata for client-side translation (e.g. chargeName, previousAmount, currentAmount) */
  metadata?: ScheduledChargeNotificationMetadata & Record<string, unknown>;
}

export interface NotificationsState {
  queue: NotificationPayload[];
  drawerOpen: boolean;
}

const initialState: NotificationsState = {
  queue: [],
  drawerOpen: false,
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    enqueueNotification: (state, action: PayloadAction<NotificationPayload>) => {
      // Check if notification with this ID already exists
      const existingNotification = state.queue.find((n) => n.id === action.payload.id);
      if (existingNotification) {
        return; // Don't add duplicate notification
      }

      const notification = {
        ...action.payload,
        persist: action.payload.persist ?? true,
        read: action.payload.read ?? false,
      };
      state.queue.push(notification);
    },
    dequeueNotification: (state, action: PayloadAction<string>) => {
      state.queue = state.queue.filter((n) => n.id !== action.payload);
    },
    markNotificationAsRead: (state, action: PayloadAction<string[]>) => {
      const ids = action.payload;
      state.queue = state.queue.map((n) => {
        if (ids.includes(n.id)) {
          return { ...n, read: true };
        }
        return n;
      });
    },
    openDrawer: (state) => {
      state.drawerOpen = true;
    },
    closeDrawer: (state) => {
      state.drawerOpen = false;
    },
    clearAllNotifications: (state) => {
      state.queue = [];
      state.drawerOpen = false;
    },
  },
});

export const {
  enqueueNotification,
  dequeueNotification,
  markNotificationAsRead,
  openDrawer,
  closeDrawer,
  clearAllNotifications,
} = notificationsSlice.actions;
export default notificationsSlice.reducer;
